import { zodResolver } from "@hookform/resolvers/zod";
import { UseMutationResult } from "@tanstack/react-query";
import { useState } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { useGetAccessPermission } from "src/hooks/useGetAccessPermission";
import { AccessResource, ActionType } from "src/types/accessControl";
import { apiErrorHandler } from "src/utils/apiErrorHandler";
import {
  CreateExpenseAndIncomeCategoryFormType,
  CreateExpenseAndIncomeCategoryResponse,
  CreateExpenseAndIncomeSubCategoryFormType,
  CreateExpenseAndIncomeSubCategoryResponse,
  CreateExpenseAndIncomeTypeFormType,
  CreateExpenseAndIncomeTypeResponse,
  EditExpenseAndIncomeCategoryFormType,
  EditExpenseAndIncomeCategoryResponse,
  EditExpenseAndIncomeSubCategoryFormType,
  EditExpenseAndIncomeSubCategoryResponse,
  EditExpenseAndIncomeTypeFormType,
  EditExpenseAndIncomeTypeResponse,
  ExpenseAndIncomeCategory,
  ExpenseAndIncomeSubCategory,
  ExpenseAndIncomeType,
} from "../api/interfaces";
import {
  useCreateExpenseAndIncomeCategory,
  useCreateExpenseAndIncomeSubCategory,
  useCreateExpenseAndIncomeType,
  useEditExpenseAndIncomeCategory,
  useEditExpenseAndIncomeSubCategory,
  useEditExpenseAndIncomeType,
} from "../api/mutations";
import { useGetExpenseAndIncomeCategory } from "../api/queries";
import {
  expenseAndIncomeCategorySchema,
  ExpenseAndIncomeCategorySchemaType,
  expenseAndIncomeSubCategorySchema,
  ExpenseAndIncomeSubCategorySchemaType,
  expenseAndIncomeTypeSchema,
  ExpenseAndIncomeTypeSchemaType,
} from "../expenseAndIncomeCategorySchema";

interface HandleSubmitCreateProps {
  form: UseFormReturn<ExpenseAndIncomeCategorySchemaType, any, any>;
  mutation: UseMutationResult<
    CreateExpenseAndIncomeCategoryResponse,
    Error,
    CreateExpenseAndIncomeCategoryFormType,
    unknown
  >;
  handleClose: () => void;
}
interface HandleSubmitCreateExpenseAndIncomeSubCategoryProps {
  expenseAndIncomeCategoryId: number;
  form: UseFormReturn<ExpenseAndIncomeSubCategorySchemaType, any, any>;
  mutation: UseMutationResult<
    CreateExpenseAndIncomeSubCategoryResponse,
    Error,
    CreateExpenseAndIncomeSubCategoryFormType,
    unknown
  >;
  handleClose: () => void;
}
interface HandleSubmitCreateExpenseAndIncomeTypeProps {
  expenseAndIncomeSubCategoryId: number;
  form: UseFormReturn<ExpenseAndIncomeTypeSchemaType, any, any>;
  mutation: UseMutationResult<
    CreateExpenseAndIncomeTypeResponse,
    Error,
    CreateExpenseAndIncomeTypeFormType,
    unknown
  >;
  handleClose: () => void;
}

interface HandleSubmitEditProps {
  id: string;
  form: UseFormReturn<ExpenseAndIncomeCategorySchemaType, any, any>;
  mutation: UseMutationResult<
    EditExpenseAndIncomeCategoryResponse,
    Error,
    EditExpenseAndIncomeCategoryFormType,
    unknown
  >;
  handleClose: () => void;
}
interface HandleSubmitEditExpenseAndIncomeSubCategoryProps {
  id: string;
  expenseAndIncomeCategoryId: number;
  form: UseFormReturn<ExpenseAndIncomeSubCategorySchemaType, any, any>;
  mutation: UseMutationResult<
    EditExpenseAndIncomeSubCategoryResponse,
    Error,
    EditExpenseAndIncomeSubCategoryFormType,
    unknown
  >;
  handleClose: () => void;
}
interface HandleSubmitEditExpenseAndIncomeTypeProps {
  id: string;
  expenseAndIncomeSubCategoryId: number;
  form: UseFormReturn<ExpenseAndIncomeTypeSchemaType, any, any>;
  mutation: UseMutationResult<
    EditExpenseAndIncomeTypeResponse,
    Error,
    EditExpenseAndIncomeTypeFormType,
    unknown
  >;
  handleClose: () => void;
}

export const handleSubmitCreateExpenseAndIncomeCategory = async ({
  form,
  mutation,
  handleClose,
}: HandleSubmitCreateProps) => {
  const values = form.getValues();

  const payload = {
    name: values.name,
    color: values.color,
  };

  try {
    await mutation.mutateAsync(payload);

    handleClose();
  } catch (error) {
    apiErrorHandler({ error, form });
  }
};
export const handleSubmitCreateExpenseAndIncomeSubCategory = async ({
  expenseAndIncomeCategoryId,
  form,
  mutation,
  handleClose,
}: HandleSubmitCreateExpenseAndIncomeSubCategoryProps) => {
  const values = form.getValues();

  const payload = {
    expenseAndIncomeCategoryId,
    name: values.name,
  };

  try {
    await mutation.mutateAsync(payload);

    handleClose();
  } catch (error) {
    apiErrorHandler({ error, form });
  }
};
export const handleSubmitCreateExpenseAndIncomeType = async ({
  expenseAndIncomeSubCategoryId,
  form,
  mutation,
  handleClose,
}: HandleSubmitCreateExpenseAndIncomeTypeProps) => {
  const values = form.getValues();

  const payload = {
    expenseAndIncomeSubCategoryId,
    name: values.name,
    color: values.color,
    type: values.type,
  };

  try {
    await mutation.mutateAsync(payload);

    handleClose();
  } catch (error) {
    apiErrorHandler({ error, form });
  }
};

const handleSubmitEdit = async ({
  id,
  form,
  mutation,
  handleClose,
}: HandleSubmitEditProps) => {
  const values = form.getValues();

  const payload = {
    id,
    name: values.name,
    color: values.color,
  };

  try {
    await mutation.mutateAsync(payload);

    handleClose();
  } catch (error) {
    apiErrorHandler({ error, form });
  }
};
const handleSubmitEditExpenseAndInventorySubCategory = async ({
  expenseAndIncomeCategoryId,
  id,
  form,
  mutation,
  handleClose,
}: HandleSubmitEditExpenseAndIncomeSubCategoryProps) => {
  const values = form.getValues();

  const payload = {
    id,
    expenseAndIncomeCategoryId,
    name: values.name,
  };

  try {
    await mutation.mutateAsync(payload);

    handleClose();
  } catch (error) {
    apiErrorHandler({ error, form });
  }
};
const handleSubmitEditExpenseAndInventoryType = async ({
  expenseAndIncomeSubCategoryId,
  id,
  form,
  mutation,
  handleClose,
}: HandleSubmitEditExpenseAndIncomeTypeProps) => {
  const values = form.getValues();

  const payload = {
    id,
    expenseAndIncomeSubCategoryId,
    name: values.name,
    color: values.color,
    type: values.type,
  };

  try {
    await mutation.mutateAsync(payload);

    handleClose();
  } catch (error) {
    apiErrorHandler({ error, form });
  }
};

const useExpenseAndIncomeCategoryHandler = () => {
  const isAllowToCreate = useGetAccessPermission({
    resource: AccessResource["Master Data - Expense and Income Category"],
    action: ActionType["Create"],
  });

  const [isShowFormModal, setIsShowFormModal] = useState(false);
  const [
    isShowExpenseAndIncomeFormModal,
    setIsShowExpenseAndIncomeSubCategoryFormModal,
  ] = useState(false);
  const [
    isShowExpenseAndIncomeTypeFormModal,
    setIsShowExpenseAndIncomeTypeFormModal,
  ] = useState(false);

  const [selectedData, setSelectedData] = useState<
    | ExpenseAndIncomeCategory
    | ExpenseAndIncomeSubCategory
    | ExpenseAndIncomeType
    | null
  >(null);
  const [selectedDataParentId, setSelectedDataParentId] = useState<
    number | null
  >(null);

  const { data, isLoading } = useGetExpenseAndIncomeCategory({});

  const form = useForm<ExpenseAndIncomeCategorySchemaType>({
    resolver: zodResolver(expenseAndIncomeCategorySchema),
  });
  const expenseAndIncomeSubCategoryForm =
    useForm<ExpenseAndIncomeSubCategorySchemaType>({
      resolver: zodResolver(expenseAndIncomeSubCategorySchema),
    });
  const expenseAndIncomeTypeForm = useForm<ExpenseAndIncomeTypeSchemaType>({
    resolver: zodResolver(expenseAndIncomeTypeSchema),
  });

  const createMutation = useCreateExpenseAndIncomeCategory();
  const editMutation = useEditExpenseAndIncomeCategory();

  const createExpenseAndIncomeSubCategoryMutation =
    useCreateExpenseAndIncomeSubCategory();
  const editExpenseAndIncomeSubCategoryMutation =
    useEditExpenseAndIncomeSubCategory();

  const createExpenseAndIncomeTypeMutation = useCreateExpenseAndIncomeType();
  const editExpenseAndIncomeTypeMutation = useEditExpenseAndIncomeType();

  const handleAdd = () => setIsShowFormModal(true);
  const handleCloseAdd = () => {
    setIsShowFormModal(false);
    form.reset({ name: "", color: "" });
  };

  const handleEdit = (data: ExpenseAndIncomeCategory) => {
    setIsShowFormModal(true);
    setSelectedData(data);
    form.reset({
      name: data?.name,
      color: data?.hex,
    });
  };
  const handleCloseEdit = () => {
    setIsShowFormModal(false);
    setSelectedData(null);
    form.reset({ name: "", color: "" });
  };

  const handleAddExpenseAndIncomeSubCategory = (parentId: number) => {
    setSelectedDataParentId(parentId);
    setIsShowExpenseAndIncomeSubCategoryFormModal(true);
  };
  const handleCloseAddExpenseAndIncomeSubCategory = () => {
    setSelectedDataParentId(null);
    setIsShowExpenseAndIncomeSubCategoryFormModal(false);
    expenseAndIncomeSubCategoryForm.reset({ name: "" });
  };

  const handleEditExpenseAndIncomeSubCategory = (
    data: ExpenseAndIncomeSubCategory
  ) => {
    setIsShowExpenseAndIncomeSubCategoryFormModal(true);
    setSelectedData(data);
    setSelectedDataParentId(data.id);

    expenseAndIncomeSubCategoryForm.reset({
      name: data?.name,
    });
  };
  const handleCloseEditExpenseAndIncomeSubCategory = () => {
    setIsShowExpenseAndIncomeSubCategoryFormModal(false);
    setSelectedData(null);
    setSelectedDataParentId(null);
    expenseAndIncomeSubCategoryForm.reset({ name: "" });
  };

  const handleAddExpenseAndIncomeType = (parentId: number) => {
    setSelectedDataParentId(parentId);
    setIsShowExpenseAndIncomeTypeFormModal(true);
  };
  const handleCloseAddExpenseAndIncomeType = () => {
    setSelectedDataParentId(null);
    setIsShowExpenseAndIncomeTypeFormModal(false);

    expenseAndIncomeTypeForm.reset({ name: "", color: "", type: "" as any });
  };

  const handleEditExpenseAndIncomeType = (data: ExpenseAndIncomeType) => {
    setIsShowExpenseAndIncomeTypeFormModal(true);
    setSelectedData(data);
    setSelectedDataParentId(data.id);

    expenseAndIncomeTypeForm.reset({
      name: data?.name,
      color: data.hex,
      type: data.type,
    });
  };
  const handleCloseEditExpenseAndIncomeType = () => {
    setIsShowExpenseAndIncomeTypeFormModal(false);
    setSelectedData(null);
    setSelectedDataParentId(null);

    expenseAndIncomeTypeForm.reset({ name: "", color: "", type: "" as any });
  };

  return {
    isLoading,
    isAllowToCreate,
    expenseAndIncomeCategory: data?.data,
    isShowFormModal,
    isShowExpenseAndIncomeFormModal,
    isShowExpenseAndIncomeTypeFormModal,
    handleAdd,
    handleEdit,
    handleAddExpenseAndIncomeSubCategory,
    handleEditExpenseAndIncomeSubCategory,
    handleAddExpenseAndIncomeType,
    handleEditExpenseAndIncomeType,
    form,
    expenseAndIncomeSubCategoryForm,
    expenseAndIncomeTypeForm,
    formType: (!selectedData ? "CREATE" : "EDIT") as "CREATE" | "EDIT",
    handleCancelFormModal: () =>
      !selectedData ? handleCloseAdd() : handleCloseEdit(),
    handleSubmitFormModal: form.handleSubmit(
      async () =>
        await (!selectedData
          ? handleSubmitCreateExpenseAndIncomeCategory({
              form,
              mutation: createMutation,
              handleClose: handleCloseAdd,
            })
          : handleSubmitEdit({
              id: `${selectedData?.id}`,
              form,
              mutation: editMutation,
              handleClose: handleCloseEdit,
            }))
    ),
    handleCancelExpenseAndIncomeSubCategoryFormModal: () =>
      !selectedData
        ? handleCloseAddExpenseAndIncomeSubCategory()
        : handleCloseEditExpenseAndIncomeSubCategory(),
    handleSubmitExpenseAndIncomeSubCategoryFormModal:
      expenseAndIncomeSubCategoryForm.handleSubmit(
        async () =>
          await (!selectedData
            ? handleSubmitCreateExpenseAndIncomeSubCategory({
                expenseAndIncomeCategoryId: selectedDataParentId as number,
                form: expenseAndIncomeSubCategoryForm,
                mutation: createExpenseAndIncomeSubCategoryMutation,
                handleClose: handleCloseAddExpenseAndIncomeSubCategory,
              })
            : handleSubmitEditExpenseAndInventorySubCategory({
                id: `${selectedData?.id}`,
                expenseAndIncomeCategoryId: selectedDataParentId as number,
                form: expenseAndIncomeSubCategoryForm,
                mutation: editExpenseAndIncomeSubCategoryMutation,
                handleClose: handleCloseEditExpenseAndIncomeSubCategory,
              }))
      ),
    handleCancelExpenseAndIncomeTypeFormModal: () =>
      !selectedData
        ? handleCloseAddExpenseAndIncomeType()
        : handleCloseEditExpenseAndIncomeType(),
    handleSubmitExpenseAndIncomeTypeFormModal:
      expenseAndIncomeTypeForm.handleSubmit(
        async () =>
          await (!selectedData
            ? handleSubmitCreateExpenseAndIncomeType({
                expenseAndIncomeSubCategoryId: selectedDataParentId as number,
                form: expenseAndIncomeTypeForm,
                mutation: createExpenseAndIncomeTypeMutation,
                handleClose: handleCloseAddExpenseAndIncomeType,
              })
            : handleSubmitEditExpenseAndInventoryType({
                id: `${selectedData?.id}`,
                expenseAndIncomeSubCategoryId: selectedDataParentId as number,
                form: expenseAndIncomeTypeForm,
                mutation: editExpenseAndIncomeTypeMutation,
                handleClose: handleCloseAddExpenseAndIncomeType,
              }))
      ),
  };
};

export default useExpenseAndIncomeCategoryHandler;
